import { ISelectType, Loading, Modal, useFetch } from "@4uhub/lib4uhub";
import { memo, useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
  IDashboardFilters,
  IListComponent,
  IListIDashboard,
} from "../../../../models/dashboard";
import DashBoardService from "../../../../services/dashboard.service";
import { usePanel } from "../../../../store/contexts/DashBoardPannelContext";
import { MIN_HEIGTH, MIN_WIDTH } from "../../Panel/Layout";
import DashBoardModalItem from "./DashboardModalItem";
import { DashboardFilters } from "./DashboardFilters/DashboardFilters";
import { DashboardComponentsSelect } from "./DashboardComponentsSelect/DashboardComponentsSelect";
import { motion } from "framer-motion";

interface IDashBoardModalProps {
  open: boolean;
  onClose: () => void;
}

// Passos do modal: escolher o dashboard -> escolher os componentes -> filtros (só se o dashboard tiver)
type TStep = "dashboards" | "components" | "filters";

const service = new DashBoardService();

const DashBoardModal: React.FC<IDashBoardModalProps> = ({ open, onClose }) => {
  const [step, setStep] = useState<TStep>("dashboards");

  const [filters, setFilters] = useState<ISelectType[]>([]);

  const [components, setComponents] = useState<IListComponent[]>([]);

  const [enabledComponentIds, setEnabledComponentIds] = useState<string[]>([]);

  const [dashboard, setDashboard] = useState<IListIDashboard | null>(null);

  const { sendRequest, loading } = useFetch(service.list);

  const { sendRequest: getFilters, loading: gLoading } = useFetch(
    service.listDashboardFilters,
  );

  const { sendRequest: getComponents, loading: cpLoading } = useFetch(
    service.listComponents,
  );

  const { sendRequest: create, loading: cLoading } = useFetch(service.create);

  const { t } = useTranslation();

  const [dashboards, setDashboards] = useState<IListIDashboard[]>([]);

  const {
    languageId,
    panel: { id: dashBoardPanelId },
    onAddDashboard,
  } = usePanel();

  const fetchDashBoards = useCallback(async () => {
    const { data, success } = await sendRequest(languageId);

    if (data && success) {
      setDashboards(data);
    }
  }, [sendRequest, languageId]);

  useEffect(() => {
    if (open) {
      fetchDashBoards();
    }
  }, [open, fetchDashBoards]);

  const createHandler = useCallback(
    async (id: string, componentIds: string[], filters?: IDashboardFilters) => {
      const { data, success } = await create({
        dashboardId: id,
        dashboardPanelId: dashBoardPanelId,
        languageId: languageId,
        height: String(MIN_HEIGTH),
        width: String(MIN_WIDTH),
        xPosition: "0",
        yPosition: "0",
        filters: filters ?? {
          ticketChannels: [],
          ticketCustomerServiceTypes: [],
          ticketServices: [],
          ticketTypes: [],
          ticketWorkspaces: [],
        },
        enabledComponentIds: componentIds,
      });

      if (data && success) {
        onClose();
        onAddDashboard(data);
      }
    },
    [dashBoardPanelId, languageId, onClose, create, onAddDashboard],
  );

  // Escolheu o dashboard: carrega componentes e filtros juntos e vai pro passo de componentes
  const selectDashboardHandler = useCallback(
    async (value: IListIDashboard) => {
      const { id } = value;

      const [componentsResult, filtersResult] = await Promise.all([
        getComponents({ DashboardId: id, languageId }),
        getFilters({ languageId, id }),
      ]);

      if (!componentsResult.success || !componentsResult.data) return;
      if (!filtersResult.success || !filtersResult.data) return;

      setDashboard(value);
      setComponents(componentsResult.data);
      setFilters(filtersResult.data);
      setEnabledComponentIds([]);
      setStep("components");
    },
    [getComponents, getFilters, languageId],
  );

  // Confirmou os componentes: segue pros filtros, ou cria direto se o dashboard não tiver filtros
  const confirmComponentsHandler = useCallback(
    (selectedIds: string[]) => {
      if (!dashboard) return;

      setEnabledComponentIds(selectedIds);

      if (filters.length > 0) {
        setStep("filters");
        return;
      }

      createHandler(dashboard.id, selectedIds);
    },
    [dashboard, filters, createHandler],
  );

  const createWithFiltersHandler = useCallback(
    (id: string, selectedFilters: IDashboardFilters) => {
      createHandler(id, enabledComponentIds, selectedFilters);
    },
    [createHandler, enabledComponentIds],
  );

  const backToDashboardsHandler = useCallback(() => {
    setStep("dashboards");
    setFilters([]);
    setComponents([]);
    setEnabledComponentIds([]);
    setDashboard(null);
  }, []);

  const backToComponentsHandler = useCallback(() => {
    setStep("components");
  }, []);

  useEffect(() => {
    if (!open) {
      backToDashboardsHandler();
      setDashboards([]);
    }
  }, [open, backToDashboardsHandler]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxDialogWidth="sm"
      title={t("components.panel.add_dashboard") + ""}
    >
      {(loading || cLoading) && (
        <Loading sx={{ position: "relative" }} size={50} />
      )}
      {!loading && !cLoading && step === "components" && dashboard && (
        <DashboardComponentsSelect
          dashboard={dashboard}
          components={components}
          initialSelected={enabledComponentIds}
          hasFilters={filters.length > 0}
          loading={cLoading}
          onBackHandler={backToDashboardsHandler}
          onConfirm={confirmComponentsHandler}
        />
      )}
      {!loading && !cLoading && step === "filters" && dashboard && (
        <DashboardFilters
          dashboard={dashboard}
          filters={filters}
          loading={cLoading}
          onCreateDashboard={createWithFiltersHandler}
          onBackHandler={backToComponentsHandler}
        />
      )}
      {!loading && !cLoading && step === "dashboards" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] }}
          style={{ maxHeight: "300px", overflow: "auto" }}
        >
          {dashboards.map((d) => (
            <DashBoardModalItem
              data={d}
              loading={cLoading || gLoading || cpLoading}
              onClick={selectDashboardHandler}
              key={d.id}
            />
          ))}
        </motion.div>
      )}
    </Modal>
  );
};

export default memo(DashBoardModal);
